import Delta from 'quill-delta';
import axiosClient from '../../../../utils/axios';
import { convertToUnicode, UnicodeStyle } from 'yay-text';
import GraphemeSplitter from 'grapheme-splitter';
import { lomavisShowToastr } from '@/utils/ui/toastr';
import { resolve } from '@/api/endPoints';

export const HASHTAG_REGEX = /(^| {1})(#[äÄüÜöÖß\w\d]+)/gim;
// export const HASHTAG_REGEX = /(^| )#[\p{L}\p{N}_äÄüÜöÖß]+/gu;
const MENTION_REGEX = /(@[äÄüÜöÖß\w\d-\.-_]+)/gi;
const MENTION_REGEX_QUILL_CONFIG =
  /^[A-Za-zÅÄÖÜåäöü\d.\w-|&+$]*(?:\s[A-Za-zÅÄÖÜåäöü\d.\w-|&+$]+)*$/i;
export const URL_REGEX =
  /(^| {1})((?:(?:(?:http|https):(?:\/\/)?)(?:[\-;:&=\+\$,\w]+)?[A-Za-z0-9\.\-]+\.\w{2,}|(?:www\.)[A-Za-z0-9\.\-]+\.\w{2,})(?:\/{0,1}[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_\*\[\]]*)#?(?:[\.\!\/\\\w\-]*)?)/gim;
export const QUILL_API_ACTION_ADD_EMOJI = 'addEmojiViaApi';
export const Lomavis_URL_SHORTENER_URL = 'https://loma.li';

function normalizeUrl(url: string): string {
  const hasProtocol = /^(https?|ftps?):\/\//i.test(url);
  return hasProtocol ? url : `https://${url}`;
}

function processHashtagBlobs(blobs) {
  const result = [];

  for (const blob of blobs) {
    const inserted = blob.insert;

    if (typeof inserted === 'string') {
      let lastIndex = 0;
      const matches = [...inserted.matchAll(HASHTAG_REGEX)];

      for (const match of matches) {
        const start = match.index;
        const fullMatch = match[0];
        const hashtag = match[2];

        // Add text before the hashtag
        if (start > lastIndex) {
          result.push({
            insert: inserted.slice(lastIndex, start),
            ...(blob.attributes ? { attributes: { ...blob.attributes } } : {})
          });
        }
        if (match[1] !== '') {
          result.push({
            insert: match[1],
            ...(blob.attributes ? { attributes: { ...blob.attributes } } : {})
          });
        }

        // Add the hashtag with special styling
        result.push({
          insert: hashtag,
          attributes: {
            creatorHashtag: hashtag
          }
        });

        lastIndex = start + fullMatch.length;
      }

      // Add the remaining text current last match
      if (lastIndex < inserted.length) {
        // Copy all attributes except creatorHashtag
        const attributes = blob.attributes
          ? Object.fromEntries(
              Object.entries(blob.attributes).filter(
                ([key]) => key !== 'creatorHashtag'
              )
            )
          : {};

        result.push({
          insert: inserted.slice(lastIndex),
          ...(Object.keys(attributes).length > 0 ? { attributes } : {})
        });
      }
    } else {
      result.push(blob);
    }
  }

  return result;
}

export function highlightQuillInput(quillInstance) {
  const contents = quillInstance.getContents();
  const originalBlobs = contents.ops;

  const blobs = processHashtagBlobs(originalBlobs);

  const opsArray = [];

  const cursorPosition = quillInstance.getSelection();
  const originalCursorIndex = cursorPosition?.index ?? null;
  const originalCursorLength = cursorPosition?.length ?? 0;

  const originalGraphemeOffset =
    originalCursorIndex !== null
      ? getGraphemeOffsetFromOps(blobs, originalCursorIndex)
      : null;
  const endGraphemeOffset = getGraphemeOffsetFromOps(
    blobs,
    originalCursorIndex + originalCursorLength
  );

  // clear out all unicode

  // build array with blobs only being mention or text
  const isHashtagBlob = (blob) => {
    return blob?.attributes?.creatorHashtag !== undefined;
  };
  for (let i = 0; i < blobs.length; i++) {
    const originalBlob = blobs[i];
    let blob = JSON.parse(JSON.stringify(originalBlob));
    let inserted = blob.insert;
    if (typeof inserted === 'string') {
      inserted = convertToUnicode(inserted, UnicodeStyle.NORMAL);
    }

    const isMention = typeof inserted !== 'string';
    // If it's text, normalize it

    if (typeof inserted === 'string' && !isHashtagBlob(blob)) {
      inserted = convertToUnicode(inserted, UnicodeStyle.NORMAL);
      //converted stylized character to plain characters
      // inserted = inserted.normalize('NFKC').replace(/\p{Mn}/gu, '');

      // Apply styles if attributes exist
      if (blob.attributes) {
        const { bold, italic, underline, strike } = blob.attributes;

        // Prioritize underline or strike (whichever is present), else apply bold/italic combo
        if (underline) {
          inserted = convertToUnicode(inserted, UnicodeStyle.MATH_UNDERLINE);
          // Clear all styles except underline
          blob.attributes = { underline: true };
        } else if (strike) {
          inserted = convertToUnicode(inserted, UnicodeStyle.MATH_STRIKE);
          // Clear all styles except strike
          blob.attributes = { strike: true };
        } else if (italic) {
          inserted = convertToUnicode(inserted, UnicodeStyle.MATH_ITALIC);
          blob.attributes = { italic: true };
        } else if (bold) {
          inserted = convertToUnicode(inserted, UnicodeStyle.MATH_BOLD);
          blob.attributes = { bold: true };
        } else {
          inserted = convertToUnicode(inserted, UnicodeStyle.NORMAL);
        }
      }

      blob.insert = inserted;

      opsArray.push(blob);
    } else if (isMention) {
      console.warn('it is a mention', blobs[i]);
      opsArray.push(JSON.parse(JSON.stringify(blobs[i])));
    } else if (isHashtagBlob(blobs[i])) {
      console.warn('it is a hashTag');
      opsArray.push(JSON.parse(JSON.stringify(blobs[i])));
    } else if (blob?.attributes?.list) {
      opsArray.push(JSON.parse(JSON.stringify(blobs[i])));
      opsArray.push({
        insert: ''
      });
    } else {
      let last_data = opsArray[opsArray.length - 1];
      if (last_data !== undefined) {
        const last_data_is_mention = typeof last_data.insert !== 'string';
        if (last_data_is_mention) {
          opsArray.push({
            insert: ''
          });
        }
      } else {
        opsArray.push({
          insert: ''
        });
      }
      last_data = opsArray[opsArray.length - 1];

      last_data.insert += inserted;
    }
  }

  // correct newline characters
  let opsArrayWithCorrectNewline = [];
  for (let i = 0; i < opsArray.length; i++) {
    const blob = opsArray[i];
    const inserted = blob.insert;
    const isMention = typeof inserted !== 'string';
    const isHashtag = isHashtagBlob(blob);
    if (isMention || isHashtag) {
      opsArrayWithCorrectNewline.push(blob);
    } else {
      const betweenNewLines = inserted.split('\n');
      for (let j = 0; j < betweenNewLines.length; j++) {
        const t = betweenNewLines[j];
        if (t.length === 0) continue;
        opsArrayWithCorrectNewline.push({
          insert: betweenNewLines[j]
        });
        opsArrayWithCorrectNewline.push({
          insert: '\n'
        });
      }
    }
  }

  // highlight- this adds black color to text now
  opsArrayWithCorrectNewline = opsArray;

  let opsArrayWithFormatting = [];
  for (let i = 0; i < opsArrayWithCorrectNewline.length; i++) {
    const blob = opsArrayWithCorrectNewline[i];
    const inserted = opsArrayWithCorrectNewline[i].insert;
    const isMention = typeof inserted !== 'string';
    const isHashtag = isHashtagBlob(blob);

    if (isMention || isHashtag) {
      opsArrayWithFormatting.push(blob);
    } else {
      for (const str of inserted.split(HASHTAG_REGEX)) {
        const isEmpty = str === '';
        if (isEmpty) continue;
        opsArrayWithFormatting.push({
          attributes: {
            bold: false,
            color: 'black',
            ...(blob.attributes?.list ? { list: blob.attributes.list } : {})
          },
          insert: str
        });
      }
    }
  }

  // highlight url

  const opsArrayWithFormatting2 = [];
  for (let i = 0; i < opsArrayWithFormatting.length; i++) {
    const blob = opsArrayWithFormatting[i];
    const inserted = blob.insert;
    const isMention = typeof inserted !== 'string';
    const isHashtag = isHashtagBlob(blob);
    if (isMention || isHashtag) {
      opsArrayWithFormatting2.push(blob);
    } else {
      for (const str of inserted.split(URL_REGEX)) {
        const isEmpty = str === '' || str === undefined;
        if (isEmpty) continue;

        const attributes_not_set = blob.attributes === undefined;
        if (attributes_not_set) continue;
        const attrs = blob.attributes;
        const is_already_marked = attrs.color != 'black';
        if (is_already_marked) {
          opsArrayWithFormatting2.push(blob);
          break;
        }
        const isURL = str.match(URL_REGEX) !== null;
        if (isURL) {
          opsArrayWithFormatting2.push({
            attributes: {
              bold: false,
              color: '#3699ff',
              link: normalizeUrl(str),
              ...(blob.attributes?.list ? { list: blob.attributes.list } : {})
            },
            insert: str
          });
        } else {
          opsArrayWithFormatting2.push({
            attributes: {
              bold: false,
              color: 'black',
              ...(blob.attributes?.list ? { list: blob.attributes.list } : {})
            },
            insert: str
          });
        }
      }
    }
  }

  // Convert Unicode back to normal text with Quill attributes

  let opsArrayWithAttributes = [];
  for (let i = 0; i < opsArrayWithFormatting2.length; i++) {
    const blob = opsArrayWithFormatting2[i];
    const isHashtag = isHashtagBlob(blob);
    if (typeof blob.insert === 'string' && !isHashtag) {
      const textSegments = convertFromUnicode(blob.insert);
      for (const segment of textSegments) {
        opsArrayWithAttributes.push({
          insert: segment.insert,
          attributes: {
            ...blob.attributes,
            ...segment.attributes
          } // Keep original attributes
        });
      }
    } else {
      opsArrayWithAttributes.push(blob);
    }
  }

  quillInstance.setContents(new Delta({ ops: opsArrayWithAttributes }), 'api');

  if (originalGraphemeOffset !== null) {
    const { index: newCursorIndex, length: currentGraphemeLength } =
      getCharIndexAndLengthFromGraphemeOffset(
        opsArrayWithAttributes,
        originalGraphemeOffset
      );
    const { index: newCursorEndIndex } =
      getCharIndexAndLengthFromGraphemeOffset(
        opsArrayWithAttributes,
        endGraphemeOffset
      );

    const newCursorLength = newCursorEndIndex - newCursorIndex;

    quillInstance.setSelection(newCursorIndex, newCursorLength);
  }
}

export function convertFromUnicode(text: string): {
  insert: string;
  attributes: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strike?: boolean;
  };
}[] {
  const segments: {
    insert: string;
    attributes: {
      bold?: boolean;
      italic?: boolean;
      underline?: boolean;
      strike?: boolean;
    };
  }[] = [];

  let currentAttributes: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strike?: boolean;
  } = {};
  let currentSegment = '';

  // this is used to split text into percieb=vable characters. This includes chacater like "ñ" or underlined letters that has multiple characters
  const charMatches = text.match(/\P{M}\p{M}*|\p{M}/gu) || [];

  for (let i = 0; i < charMatches.length; i++) {
    const char = charMatches[i];
    const nextChar = charMatches[i + 1] || '';

    //transforms characters to a canonical form that simplifies similar-looking characters into one standard representation. Basically converted stylized character to plain letters
    const plainChar = convertToUnicode(char, UnicodeStyle.NORMAL);

    let attributes: {
      bold?: boolean;
      italic?: boolean;
      underline?: boolean;
      strike?: boolean;
    } = {};

    if (char !== plainChar) {
      if (char === convertToUnicode(plainChar, UnicodeStyle.MATH_BOLD)) {
        attributes.bold = true;
      } else if (
        char === convertToUnicode(plainChar, UnicodeStyle.MATH_ITALIC)
      ) {
        attributes.italic = true;
      }
      if (char === convertToUnicode(plainChar, UnicodeStyle.MATH_UNDERLINE)) {
        attributes.underline = true;
      }
      if (char === convertToUnicode(plainChar, UnicodeStyle.MATH_STRIKE)) {
        attributes.strike = true;
      }
    } else if (char === ' ') {
      // Single space continues current style
      if (nextChar !== ' ') {
        attributes = { ...currentAttributes };
      } else {
        // Double space — reset style
        attributes = {};
      }
    } else if (char === '\n') {
      attributes = {}; // Break styling on newline
    }

    const attributesChanged =
      JSON.stringify(attributes) !== JSON.stringify(currentAttributes);

    if (attributesChanged) {
      if (currentSegment) {
        segments.push({
          insert: currentSegment,
          attributes: { ...currentAttributes }
        });
      }
      currentSegment = char;
      currentAttributes = attributes;
    } else {
      currentSegment += char;
    }
  }

  if (currentSegment) {
    segments.push({
      insert: currentSegment,
      attributes: { ...currentAttributes }
    });
  }

  return segments;
}

const splitter = new GraphemeSplitter();

function getGraphemeOffsetFromOps(ops, index) {
  let seenChars = 0;
  let seenGraphemes = 0;

  for (const op of ops) {
    // Mentions (and any other embeds) are represented as objects. Quill counts
    // them as length 1, so we need to count them toward both char and grapheme
    // totals to keep cursor math aligned with Quill's internal representation.
    if (typeof op.insert !== 'string') {
      const nextSeenChars = seenChars + 1;
      if (index <= nextSeenChars) {
        return seenGraphemes + 1;
      }
      seenChars = nextSeenChars;
      seenGraphemes += 1;
      continue;
    }

    const text = op.insert;
    const textLength = text.length;
    const nextSeenChars = seenChars + textLength;

    if (index <= nextSeenChars) {
      const localIndex = index - seenChars;
      const sub = text.slice(0, localIndex);
      return seenGraphemes + splitter.countGraphemes(sub);
    }

    seenChars = nextSeenChars;
    seenGraphemes += splitter.countGraphemes(text);
  }

  return seenGraphemes;
}
function getCharIndexAndLengthFromGraphemeOffset(ops, targetGraphemeOffset) {
  let charIndex = 0;
  let graphemeCount = 0;

  for (const op of ops) {
    // Treat embeds (mentions) as a single grapheme and single char to mirror
    // Quill's selection indexing.
    if (typeof op.insert !== 'string') {
      if (graphemeCount === targetGraphemeOffset) {
        return { index: charIndex, length: 1 };
      }
      charIndex += 1;
      graphemeCount += 1;
      continue;
    }

    const graphemes = splitter.splitGraphemes(op.insert);
    for (const g of graphemes) {
      if (graphemeCount === targetGraphemeOffset) {
        return { index: charIndex, length: g.length };
      }
      charIndex += g.length;
      graphemeCount++;
    }
  }

  // Fallback to end
  return { index: charIndex, length: 0 };
}

export function setCursorPositionForQuill(quillInstance) {
  const selection = quillInstance.getSelection();
  const contents = quillInstance.getContents();
  const blobs = contents.ops;

  let cursorPosition;
  let newCursorIndex;

  const originalCursorIndex = cursorPosition?.index ?? null;
  const originalGraphemeOffset =
    originalCursorIndex !== null
      ? getGraphemeOffsetFromOps(blobs, originalCursorIndex)
      : null;

  if (selection) {
    cursorPosition = selection.index + selection.length;

    newCursorIndex = selection.index + selection.length;
  } else {
    newCursorIndex = removeTrailingNewlineCharacter(
      quillInstance.getText()
    ).length;
  }

  quillInstance.container.dataset.cursorPosition = newCursorIndex;
}

function removeTrailingNewlineCharacter(text) {
  return text.replace(/\n$/, '');
}

export const highlightFunctionAndQuillCopy = (
  allPlatformTextQuillInstance,
  seperateQuill
) => {
  const contents = allPlatformTextQuillInstance.getContents();
  const blobs = contents.ops;
  highlightQuillInput(allPlatformTextQuillInstance);

  for (let j = 0; j < seperateQuill.length; j++) {
    const quillInstance = seperateQuill[j];
    const platform = quillInstance.container.dataset.platform;

    const new_content = [];
    for (let i = 0; i < blobs.length; i++) {
      const blob = blobs[i];
      const inserted = blobs[i].insert;
      const isMention = typeof inserted !== 'string';

      if (isMention) {
        const mentionBelongsToPlatform =
            inserted.mention.platform.indexOf(platform) > -1;
        if (mentionBelongsToPlatform) {
          new_content.push(blob);
        } else {
          // mention does not belong to this platform -> we need handle the auto included space properly
          const nextBlob = blobs[i + 1] || {};
          const nextBlobInsert = nextBlob.insert || '';
          const secondNextBlob = blobs[i + 2] || {};
          const secondNextBlobInsert = secondNextBlob.insert || '';
          const nextBlobIsMention = typeof nextBlobInsert !== 'string';
          const secondNextBlobIsMention =
              typeof secondNextBlobInsert !== 'string';
          if (!nextBlobIsMention) {
            const leadingCharIsWhiteSpace = nextBlobInsert.slice(0, 1) === ' ';
            if (leadingCharIsWhiteSpace) {
              // remove leading space from next blob
              new_content.push({ insert: nextBlobInsert.slice(1) });
            } else {
              new_content.push({ insert: nextBlobInsert });
            }
            // we modified next blob, let's skip the next iteration
            i++;
          }
        }
      } else {
        new_content.push(blob);
      }
    }
    quillInstance.setContents(new_content, 'user');
    // quillInstance.insertText(quillInstance.getLength(), '', 'user') // force run of highlighting
  }

  // exportQuillToTextField(allPlatformTextQuillInstance)
};

const linkPreviewCache = {};
const LOADING_LINK_PREVIEW = 'loadingLink';
const sleep = async (ms) => await new Promise((res) => setTimeout(res, ms));
const LINK_PREVIEW_API_URL = '/api/v1/posts/link_preview/';
function buildLinkPreviewApiUrl(url) {
  return `${LINK_PREVIEW_API_URL}?url=${encodeURIComponent(url)}`;
}
export async function fetchLinkPreview(url) {
  return await new Promise(async (resolve, reject) => {
    const urlIsInCache = Object.keys(linkPreviewCache).includes(url);
    let data = {};

    if (urlIsInCache) {
      data = linkPreviewCache[url];

      // when an url is entered the preview data needs to be fetched for every platform.
      // If one request is already being send to the backend, the other requests will
      // wait for data to be loaded into the cache and then use the cached data. This wax
      // is to prevent multiple requests to the backend for the same url.
      const dataIsStillLoading = data === LOADING_LINK_PREVIEW;
      if (dataIsStillLoading) {
        for (let i = 0; i < 2000; i++) {
          await sleep(100);
          data = linkPreviewCache[url];
          if (data !== LOADING_LINK_PREVIEW) {
            break;
          }
        }
      }
    } else {
      linkPreviewCache[url] = LOADING_LINK_PREVIEW;
      let retry = 0;
      const MAX_RETRIES = 6;
      let err;
      while (retry < MAX_RETRIES) {
        err = undefined;
        try {
          // data = await (await fetch(buildLinkPreviewApiUrl(url))).json()
          data = await axiosClient.get(buildLinkPreviewApiUrl(url));
          break;
        } catch (e) {
          err = e;
          console.log('Failed to fetch preview api - retrying', err);
          retry++;
        }
      }

      if (err) {
        console.error(
            'Failed to fetch preview api - not retrying anymore',
            err
        );
        // Sentry.captureException(err)
      }

      linkPreviewCache[url] = data;
    }

    resolve(data);
  });
}
export const getUrlFromText = (text) => {
  const urlRegex = URL_REGEX;

  const urls = text.match(urlRegex);

  if (!urls) {
    return null;
  }
  return urls[0].trim();
};

export const getUrlPreview = async (text) => {
  const url = getUrlFromText(text);
  if (!url) {
    return null;
  }
  const response: any = await fetchLinkPreview(url);
  if (response.data.count === 0) {
    return { error: true };
  }
  return { ...response.data.results[0], error: false };
};

export function getTextFromQuill(quillInstance, excludeMentions = false) {
  if (!quillInstance) {
    return;
  }
  const blobs = quillInstance.getContents();
  let text = '';
  for (const d of blobs.ops) {
    const inserted = d.insert;
    const isMention = typeof inserted !== 'string';
    if (isMention) {
      if (excludeMentions) {
        continue;
      }
      text += inserted.mention.show_denotation_char === 'true' ? '@' : '';
      text += inserted.mention.display_name;
    } else {
      text += inserted;
    }
  }
  text = text.replace(/\n$/, '');
  return text;
}

export function trimTrailingNewline(delta) {
  // Clone the delta deeply to avoid mutation issues
  const clonedDelta = new Delta(JSON.parse(JSON.stringify(delta)));

  const ops = clonedDelta.ops;
  if (Array.isArray(ops)) {
    const lastOp = ops[ops.length - 1];

    if (lastOp?.insert === '\n' && Object.keys(lastOp).length === 1) {
      // Remove trailing newline
      ops.pop();
    }
  }

  return clonedDelta;
}

type LinkOp = {
  insert: string;
  attributes: {
    link: true;
    color?: string;
    bold?: boolean;
    list?: any;
  };
};

export async function processLinksInQuill(
  quill: any,
  apiCall: (linkText: string) => Promise<string>,
  onSuccess?: () => void
) {
  const delta = quill.getContents();

  const newOps = await Promise.all(
    (delta.ops || []).map(async (op: any, idx: number) => {
      try {
        const isURL = op.insert.match(URL_REGEX) !== null;

        if (op.attributes?.link || isURL) {
          let originalLink = op.insert?.trim();
          if (!op.insert || originalLink === '') return op;

          // If already shortened, skip
          if (originalLink.startsWith(Lomavis_URL_SHORTENER_URL)) {
            return op;
          }

          // console.log(`Processing op[${idx}] with link:`, op.insert);

          const newInsert = await apiCall(op.insert);

          return {
            insert: newInsert,
            attributes: op.attributes
          };
        }

        return op; // non-link ops
      } catch (error) {
        console.error(`Error processing op[${idx}]:`, error);
        return op;
      }
    })
  );
  onSuccess();

  const newDelta = { ops: newOps };
  quill.setContents(newDelta, 'user');
}

export async function shortenLink(linkText: string): Promise<string> {
  try {
    // Normalize protocol if missing
    const hasProtocol = /^(https?:\/\/|ftps?:\/\/)/i.test(linkText);
    const normalizedLink = hasProtocol ? linkText : `https://${linkText}`;
    const url = resolve('url_shortener', {}, {});

    const response = await axiosClient.post(
      url,
      JSON.stringify({ url: normalizedLink }),
      {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 5000 // add a timeout (in milliseconds)
      }
    );

    const result = response?.data;

    if (!result) {
      throw new Error('Shortened URL not returned');
    }
    if (!result.shortened_url && result?.message) {
      lomavisShowToastr(result?.message, 'error');
      throw new Error('Shortened URL not returned');
    }

    return result.shortened_url;
  } catch (error) {
    console.error(`Error shortening link "${linkText}":`, error);
    const result = error?.response?.data;

    if (!result.shortened_url && result?.message) {
      lomavisShowToastr(result?.message, 'error');
      throw new Error('Shortened URL not returned');
    }
    throw error; // re-throw so caller (e.g., processLinksInQuill) can handle with toast
  }
}

// --- helpers ---
function utf8ByteLen(str: string): number {
  return new TextEncoder().encode(str).length;
}

function segmentGraphemes(text: string): string[] {
  if ((Intl as any).Segmenter) {
    const seg = new (Intl as any).Segmenter('en', { granularity: 'grapheme' });
    return Array.from(seg.segment(text), (s: any) => s.segment);
  }
  // Fallback (not perfect for all emoji sequences, but reasonable)
  return Array.from(text);
}

// Broad "emoji cluster" detector: any cluster with an Emoji / Extended_Pictographic code point.
// Works on modern browsers with Unicode property escapes.
const EMOJI_IN_CLUSTER =
  /[\p{Extended_Pictographic}\p{Emoji_Presentation}\p{Emoji}]/u;

// --- your original "raw string" builder ---

/**
 * Count length like your backend-visible string:
 * - Start with the escaped representation length (so \n and \u200d etc. are counted literally)
 * - Then adjust ONLY emoji grapheme clusters to use their full UTF-8 byte length
 */
export function countAllCharacters(quill: any): number {
  // 1) Base: backend-like escaped string length
  const delta = quill.getContents();
  let fullText = '';
  delta.ops.forEach((op: any) => {
    if (typeof op.insert === 'string') {
      fullText += op.insert;
    }
  });
  const rawEscaped = JSON.stringify(fullText).slice(1, -1);
  let total = rawEscaped.length;

  // 2) Adjust ONLY emoji clusters to UTF-8 byte length
  //    Base count currently counts emoji as their JS string length (code units) inside rawEscaped.
  //    We add the difference: utf8Bytes(emojiCluster) - codeUnitLength(emojiCluster).
  const clusters = segmentGraphemes(fullText);

  for (const cluster of clusters) {
    if (EMOJI_IN_CLUSTER.test(cluster)) {
      const jsUnits = cluster.length; // how many code units rawEscaped originally accounted for
      const utf8 = utf8ByteLen(cluster); // desired count for this emoji cluster
      total += utf8 - jsUnits;
    }
  }

  return total;
}
